<template>
  <div ref="blockRef" class="mx-auto px-4 max-w-6xl lg:flex lg:flex-col lg:justify-center lg:min-h-screen">
    <h2
      v-if="block.title"
      v-html="formatTitle(block.title)"
      :class="`text-[clamp(1.5rem,0.8945rem+3.0275vw,5.625rem)] text-center leading-none whitespace-nowrap ${block.title_color.value} [&>span]:inline-block`"
      data-gsap-target="title"
    />

    <Buttons
      v-if="block.buttons?.length"
      :buttons="block.buttons"
      class="flex justify-center mt-8"
      data-gsap-target="buttons"
    />

    <ClientOnly>
      <Overlay gtm-overlay-name="course">
        <div>
          <div class="font-serif font-bold text-[clamp(1.5rem,-1.4831rem+14.9153vw,4.25rem)] text-purple-dark leading-none">
            Aanmelden
          </div>

          <div v-if="entry.title || entry.price" class="flex flex-wrap gap-x-6 mt-2">
            <div v-if="entry.title">
              Training: {{ entry.title }}
            </div>

            <div v-if="entry.price">
              Prijs: {{ entry.price }}
            </div>
          </div>

          <div class="prose mt-4">
            Zit je met vragen? Bel of stuur ons een e-mail, we staan je graag te woord.
          </div>

          <Form
            v-if="filterPastCourseDates(entry.course_dates)?.length"
            class="mt-12"
            handle="course"
            :form="modifiedForm"
            :redirect-url="block.entry_after_submit?.url"
            :additional-fields="{
              'current_url': useRoute().path,
            }"
            :submit-button-text="$statamic.trans('form_course_submit_button_text')"
          />

          <div v-else class="mt-12 font-bold">
            {{ $statamic.trans('no_course_dates') }}
          </div>
        </div>

        <div
          v-if="entry.employee"
          class="hidden flex-col items-center self-start p-6 w-full max-w-md text-center bg-purple-dark rounded-3xl md:p-12 md:rounded-5xl lg:sticky lg:top-0 lg:flex"
        >
          <div
            v-if="entry.employee.image"
            class="mx-auto size-32 rounded-full border-2 border-white bg-gradient-employee overflow-hidden xl:size-36"
          >
            <img
              :src="entry.employee.image.sm.url"
              :alt="entry.employee.image.sm.alt"
              class="size-full object-cover"
              :width="entry.employee.image.sm.width"
              :height="entry.employee.image.sm.height"
              loading="lazy"
              draggable="false"
            >
          </div>

          <div v-if="entry.employee.title" class="mt-10 font-serif font-bold text-white text-xl">
            {{ entry.employee.title }}
          </div>

          <div v-if="entry.employee.position" class="mt-1 text-white text-xs md:text-sm">
            {{ entry.employee.position }}
          </div>

          <div class="mt-4 text-white text-xs md:text-sm">
            Meer weten? Stel je vraag.<br>
            Neem contact op.
          </div>

          <div v-if="entry.employee.email" class="flex items-center mt-6">
            <a
              :href="`mailto:${entry.employee.email}`"
              class="btn btn--primary"
              role="button"
            >
              Stuur mij een bericht
            </a>
          </div>
        </div>
      </Overlay>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep';
import type { Entry } from '~/statamic/types/entry';
import type { CourseSignupForm } from '~/statamic/types/block';

const props = defineProps<{
  entry: Entry;
  block: CourseSignupForm;
}>();

const form = await useStatamicForm({ handle: 'course' });

const dateFieldOptions = {};

filterPastCourseDates(props.entry.course_dates).forEach((date) => {
  const formattedDate = formatDate(date.course_dates_date, true);

  dateFieldOptions[formattedDate] = formattedDate;
});

const modifiedForm = computed(() => {
  const clonedForm = cloneDeep(form);

  clonedForm.fields.forEach((field) => {
    if (field.handle === 'date') {
      // eslint-disable-next-line no-param-reassign
      field.config.options = dateFieldOptions;
    }
  });

  return clonedForm;
});

let ctx: gsap.Context | null;
const blockRef = ref();

onMounted(() => {
  ctx = useGsap.context((self) => {
    if (typeof self.selector !== 'function') return;

    const firstTitle = self.selector('[data-gsap-target="title"]');
    const secondTitle = self.selector('[data-gsap-target="title"] > span');
    const buttons = self.selector('[data-gsap-target="buttons"]');

    const timeline = useGsap.timeline({
      scrollTrigger: {
        pin: isDesktop(),
        start: `top ${isDesktop() ? 'top' : 'bottom'}`,
        trigger: blockRef.value,
      },
    });

    if (firstTitle) {
      timeline.from(firstTitle, {
        x: -100,
        autoAlpha: 0,
      });
    }

    if (secondTitle) {
      timeline.from(secondTitle, {
        x: 100,
        autoAlpha: 0,
      });
    }

    if (buttons?.length) {
      timeline.from(buttons, {
        y: 50,
        autoAlpha: 0,
      });
    }
  }, blockRef.value);
});

onUnmounted(() => {
  ctx?.revert();
});
</script>
